import React, { useState, useEffect } from "react";
import {
  SAFE_OR_GAMBLE,
  GAIN_LOSS,
  maxBaseJitter,
  minBaseJitter,
  maxBetPercent,
  minBetPercent,
} from "./App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faDice } from "@fortawesome/free-solid-svg-icons";

export function roundTwoDigits(number) {
  return (
    Math.round(number / Math.pow(10, Math.floor(Math.log10(number)) - 1)) *
    Math.pow(10, Math.floor(Math.log10(number)) - 1)
  );
}

export function convertToDollarsWithText(number) {
  if (number === 0) {
    return "$0";
  }
  let displayValue;
  if (number >= 1e9) {
    displayValue = `$${number / 1e9} billion`;
  } else if (number >= 1e6) {
    displayValue = `$${number / 1e6} million`;
  } else {
    displayValue = `$${number.toLocaleString()}`;
  }
  return displayValue;
}

export function convertToDollars(number) {
  if (number === 0) {
    return "$0";
  } else if (number < 0) {
    return `-$${Math.abs(number).toLocaleString()}`;
  }
  return `$${number.toLocaleString()}`;
}

function calculateBet(pricePoint, riskLevel) {
  const baseJitter =
    Math.random() * (maxBaseJitter - minBaseJitter) + minBaseJitter;
  const baseAmount = baseJitter * pricePoint;
  const betPercent =
    Math.random() * (maxBetPercent - minBetPercent) + minBetPercent;
  const riskLevelExponent = riskLevel / 6;
  const betAmount = baseAmount / Math.pow(betPercent, 1 / riskLevelExponent);
  return [baseAmount, betAmount, betPercent];
}

function generateQuestionAmounts(gainLoss, pricePoint, riskLevel) {
  const optionAmounts = calculateBet(pricePoint, riskLevel);
  const baseAmount = optionAmounts[0];
  const betAmount = optionAmounts[1];
  const betPercent = optionAmounts[2];
  return [baseAmount, betAmount, betPercent, gainLoss];
}

function questionAmountsToText(baseAmount, betAmount, betPercent, gainLoss) {
  const baseAmountRounded = convertToDollarsWithText(
    roundTwoDigits(baseAmount)
  );
  const betAmountRounded = convertToDollarsWithText(roundTwoDigits(betAmount));
  const betPercentRounded = Math.round(betPercent * 100);
  const gainLossBaseText = gainLoss === 0 ? "Receive " : "Pay ";
  const gainLossBetText = gainLoss === 0 ? "win " : "lose ";
  const baseBetText = gainLossBaseText + baseAmountRounded;
  const gambleBetText =
    "Have a " +
    betPercentRounded +
    "% chance to " +
    gainLossBetText +
    betAmountRounded;
  return [
    baseBetText,
    gambleBetText,
    baseAmountRounded,
    betAmountRounded,
    betPercentRounded,
  ];
}

export function randomlyGetBetOutcome(
  choice,
  baseAmount,
  betAmount,
  betPercent,
  gainLoss
) {
  if (choice === SAFE_OR_GAMBLE.SAFE) {
    return gainLoss === GAIN_LOSS.GAIN ? baseAmount : -1 * baseAmount;
  } else {
    const randomValue = Math.random();
    if (randomValue < betPercent) {
      return gainLoss === GAIN_LOSS.GAIN ? betAmount : -1 * betAmount;
    } else {
      return 0;
    }
  }
}

const Question = ({ onChoice, gainLoss, pricePoint, riskLevel }) => {
  const [questionData, setQuestionData] = useState(null);
  useEffect(() => {
    const questionAmounts = generateQuestionAmounts(
      gainLoss,
      pricePoint,
      riskLevel
    );
    const questionText = questionAmountsToText(
      questionAmounts[0],
      questionAmounts[1],
      questionAmounts[2],
      questionAmounts[3]
    );
    const baseAmount = roundTwoDigits(questionAmounts[0]);
    const betAmount = roundTwoDigits(questionAmounts[1]);
    const betPercent = questionAmounts[2];
    const betOutcomeSafe = randomlyGetBetOutcome(
      SAFE_OR_GAMBLE.SAFE,
      baseAmount,
      betAmount,
      betPercent,
      gainLoss
    );
    const betOutcomeGamble = randomlyGetBetOutcome(
      SAFE_OR_GAMBLE.GAMBLE,
      baseAmount,
      betAmount,
      betPercent,
      gainLoss
    );
    setQuestionData({
      baseAmount,
      betAmount,
      betPercent,
      questionText,
      betOutcomeSafe,
      betOutcomeGamble,
      gainLoss,
      pricePoint,
      riskLevel,
    });
  }, [gainLoss, pricePoint, riskLevel]);

  if (!questionData) return <div>Loading...</div>;
  return (
    <div className={"button-container"}>
      <button
        className={"button button-left"}
        onClick={() =>
          onChoice(
            SAFE_OR_GAMBLE.SAFE,
            questionData.gainLoss,
            questionData.pricePoint,
            questionData.riskLevel,
            questionData.betOutcomeSafe
          )
        }
      >
        <FontAwesomeIcon icon={faLock} /> {questionData.questionText[0]}
      </button>
      <button
        className={"button button-right"}
        onClick={() =>
          onChoice(
            SAFE_OR_GAMBLE.GAMBLE,
            questionData.gainLoss,
            questionData.pricePoint,
            questionData.riskLevel,
            questionData.betOutcomeGamble
          )
        }
      >
        <FontAwesomeIcon icon={faDice} /> {questionData.questionText[1]}
      </button>
    </div>
  );
};

export default Question;
